// Doc-Ref: docs/features/collaborations-current.md
import React from "react";
import { Link } from "react-router-dom";
import SectionCard from "./SectionCard";
import { Badge } from "components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";

const CollaborationsCard = ({
  data,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSeeMore,
}) => {
  const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);

  return (
    <SectionCard
      title="Top Collaborations"
      description="Institutions with the highest volume of co-authored research"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!items || items.length === 0}
      onRetry={onRetry}
      onSeeMore={onSeeMore}
      seeMoreText="View collaborations"
    >
      <div className="flex flex-col gap-3">
        {items.slice(0, 5).map((partner, idx) => {
          const identifier = partner.explore_slug || partner.partner_id;
          const logoUrl = (partner.partner_id ? `https://media.academic.online/institutions_images/${partner.partner_id}/seal.png` : null) || partner.logo_url;

          return (
            <div
              key={partner.partner_id || idx}
              className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group"
            >
              <Link to={`/explore/institution/${identifier}`} className="shrink-0">
                <Avatar className="w-12 h-12 rounded-lg bg-white border border-border/50 p-0.5">
                  <AvatarImage
                    src={logoUrl}
                    alt={partner.partner_name}
                    className="object-contain"
                  />
                  <AvatarFallback className="bg-explore/5 text-explore font-bold rounded-md">
                    {partner.partner_name?.substring(0, 2).toUpperCase() || "UN"}
                  </AvatarFallback>
                </Avatar>
              </Link>

              <div className="flex-1 min-w-0">
                <Link to={`/explore/institution/${identifier}`} className="hover:underline">
                  <h4 className="font-semibold text-sm line-clamp-1 group-hover:text-explore transition-colors">
                    {partner.partner_name}
                  </h4>
                </Link>
              </div>

              <div className="text-right shrink-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="bg-explore/5 text-explore border-explore/20 font-semibold cursor-help whitespace-nowrap">
                        {partner.joint_pubs != null ? `${partner.joint_pubs.toLocaleString()} Pubs` : "Collaborator"}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="font-semibold text-xs mb-0.5">Joint Publications</p>
                      <p className="text-xs max-w-[180px]">Total co-authored publications with this institution.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default CollaborationsCard;
